import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib/cn';
import { sameOffset, toDate, useFormatters, type DateInput } from '@shared/lib/i18n/formatters';

export interface TimeWithOtherZoneProps {
  at: DateInput;
  /** IANA zone of the other party (teacher, student). */
  otherTimeZone: string;
  /** Who the secondary line is for ("Ustadh Kareem"); defaults to the zone's city. */
  otherLabel?: string | undefined;
  /** Include the date in the primary line. */
  withDate?: boolean;
  /** Override the viewer zone (defaults to the profile/browser zone). */
  viewerTimeZone?: string;
  className?: string;
}

/**
 * Viewer's time first, the counterpart's local time as a secondary line
 * (UX principle 3: "time is always shown in your time").
 */
export function TimeWithOtherZone({ at, otherTimeZone, otherLabel, withDate, viewerTimeZone, className }: TimeWithOtherZoneProps) {
  const { t } = useTranslation('common');
  const f = useFormatters();
  const viewerZone = viewerTimeZone ?? f.timeZone;
  const instant = toDate(at);
  const primary = withDate ? f.date(instant, 'dateTimeMedium', viewerZone) : f.time(instant, viewerZone);
  const differs = !sameOffset(viewerZone, otherTimeZone, instant);

  return (
    <span className={cn('inline-flex flex-col', className)}>
      <span className="text-primary">
        <time dateTime={instant.toISOString()}>
          <bdi className="tabular">{primary}</bdi>
        </time>{' '}
        <span className="text-sm text-muted">{t('time.yourTime')}</span>
      </span>
      {differs ? (
        <span className="text-xs text-muted">
          <bdi>
            {t('time.otherParty', {
              time: f.time(instant, otherTimeZone),
              name: otherLabel ?? f.zoneCity(otherTimeZone),
              zone: `${f.zoneCity(otherTimeZone)} · ${f.offsetLabel(otherTimeZone, instant)}`,
            })}
          </bdi>
        </span>
      ) : null}
    </span>
  );
}
